/**
 * LunarSolar Component
 *
 * This component displays lunar and solar data in simple text format.
 *
 * @component
 * @example
 * <LunarSolar lunarSolarData={data} loading={false} error={null} />
 */

import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import MoonTimeline from "./MoonTimeline";
import SunTimeline from "./SunTimeline";
import { formatTime } from "./utils";

// Import moon phase images
import NewMoon from "./Moon Phase/New_Moon.jpg";
import WaxingCrescent from "./Moon Phase/Waxing_Crescent_Moon.jpg";
import FirstQuarter from "./Moon Phase/First_Quarter_Moon.jpg";
import WaxingGibbous from "./Moon Phase/Waxing_Gibbous_Moon.jpg";
import FullMoon from "./Moon Phase/Full_Moon.jpg";
import WaningGibbous from "./Moon Phase/Waning_Gibbous.jpg";
import LastQuarter from "./Moon Phase/Last_Quarter.png";
import WaningCrescent from "./Moon Phase/Waning_Crescent.jpg";

// Moon phase image mapping
const moonPhaseImages = {
  "new moon": NewMoon,
  "waxing crescent": WaxingCrescent,
  "first quarter": FirstQuarter,
  "waxing gibbous": WaxingGibbous,
  "full moon": FullMoon,
  "waning gibbous": WaningGibbous,
  "last quarter": LastQuarter,
  "waning crescent": WaningCrescent,
};

// Common styles
const commonBoxStyle = {
  p: 1,
  border: "1px solid rgba(5, 5, 5, 0.1)",
  borderRadius: 2,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.5,
};

const imageStyle = {
  borderRadius: 2,
  boxShadow: "0 2px 2px ",
};

// Reusable Moon Phase Box Component
const MoonPhaseBox = ({
  imageSrc,
  title,
  subtitle,
  imageSize = 80,
  illumination,
}) => {
  const responsiveImageSize =
    typeof imageSize === "object"
      ? { xs: imageSize.xs || 60, sm: imageSize.sm || 80 }
      : { xs: imageSize, sm: imageSize };

  return (
    <Box sx={commonBoxStyle}>
      {imageSrc && (
        <Box
          component="img"
          src={imageSrc}
          alt={title}
          mb={0.5}
          sx={{
            width: { xs: responsiveImageSize.xs, sm: responsiveImageSize.sm },
            height: { xs: responsiveImageSize.xs, sm: responsiveImageSize.sm },
            ...imageStyle,
          }}
        />
      )}
      <Typography
        variant="caption"
        sx={{ fontSize: { xs: "0.7rem", sm: "0.9rem" } }}
        textAlign="center"
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="h6"
          sx={{ fontSize: { xs: "0.65rem", sm: "0.8rem" } }}
          textAlign="center"
          color="primary"
        >
          {subtitle}
          {illumination && (
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: "0.65rem", sm: "0.8rem" },
              }}
              textAlign="center"
              color="text.secondary"
            >
              ( Illumination: {illumination} )
            </Typography>
          )}
        </Typography>
      )}
    </Box>
  );
};

// Reusable Event Box Component
const EventBox = ({ imageSrc, title, time }) => (
  <Box sx={commonBoxStyle}>
    {imageSrc && (
      <Box
        component="img"
        src={imageSrc}
        alt={title}
        sx={{
          width: { xs: 50, sm: 80 },
          height: { xs: 50, sm: 80 },
          ...imageStyle,
          mb: 0.5,
        }}
      />
    )}
    <Typography
      variant="caption"
      sx={{ fontSize: { xs: "0.7rem", sm: "0.9rem" } }}
      textAlign="center"
    >
      {title}
    </Typography>
    <Typography
      variant="h6"
      sx={{ fontSize: { xs: "0.65rem", sm: "0.8rem" } }}
      textAlign="center"
      color="text.secondary"
    >
      {time}
    </Typography>
  </Box>
);

/**
 * Main LunarSolar component that displays lunar and solar information
 * @param {Object} props - Component props
 * @param {Object} props.lunarSolarData - Lunar and solar data
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message
 * @returns {JSX.Element} The rendered LunarSolar component
 */
function LunarSolar({ lunarSolarData, loading, error, padding = 2 }) {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }
// as in some case it gives {error: "'Moon' is below the horizon at 2025/7/30 22:01:03"}
  if (error) {
    return null;
  }

  if (!lunarSolarData) {
    return null;
  }

  // Get moon phase image
  const getMoonPhaseImage = () => {
    const phaseLower = lunarSolarData.moon_phase?.toLowerCase() || "";

    for (const [key, imagePath] of Object.entries(moonPhaseImages)) {
      if (phaseLower.includes(key)) {
        return imagePath;
      }
    }

    // if no match
    return null;
  };

  // Event data for upcoming events
  const upcomingEvents = [
    {
      key: "new moon",
      title: "Next New Moon",
      time: formatTime(lunarSolarData.next_new_moon),
    },
    {
      key: "full moon",
      title: "Next Full Moon",
      time: formatTime(lunarSolarData.next_full_moon),
    },
    {
      key: "first quarter",
      title: "Next First Quarter",
      time: formatTime(lunarSolarData.next_first_quarter),
    },
    {
      key: "last quarter",
      title: "Next Last Quarter",
      time: formatTime(lunarSolarData.next_last_quarter),
    },
  ];

  return (
    <Box sx={{ width: "100%", p: padding }}>
      <Card sx={{ mb: 0 }}>
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          {/* Main Content with Moon Phase Image */}
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              {/* Moon Timeline */}
              <Grid item xs={12} md={6}>
                <MoonTimeline lunarSolarData={lunarSolarData} />
              </Grid>

              {/* Sun Timeline */}
              <Grid item xs={12} md={6}>
                <SunTimeline lunarSolarData={lunarSolarData} />
              </Grid>
            </Grid>
          </Box>

          {/* Key Events */}
          <Box
            sx={{
              p: { xs: 0.5, sm: 1 },
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: { xs: 0.25, sm: 0.5 },
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              {/* Current Moon Phase - 25% on desktop, full width on mobile */}
              <Box
                sx={{
                  width: { xs: "100%", sm: "25%" },
                  mb: { xs: 1, sm: 0 },
                }}
              >
                <MoonPhaseBox
                  imageSrc={getMoonPhaseImage()}
                  title="Current Phase"
                  subtitle={lunarSolarData.moon_phase}
                  imageSize={{ xs: 60, sm: 80 }}
                  illumination={lunarSolarData.illumination_percent}
                />
              </Box>

              {/* Vertical Divider - only visible on desktop */}
              <Box
                sx={{
                  display: { xs: "none", sm: "block" },
                  width: "1px",
                  backgroundColor: "divider",
                  mx: 1,
                }}
              />

              {/* Upcoming Events - 75% on desktop, full width on mobile */}
              <Box
                sx={{
                  width: { xs: "100%", sm: "75%" },
                }}
              >
                <Grid container spacing={{ xs: 0.5, sm: 1 }}>
                  {upcomingEvents.map((event, index) => (
                    <Grid item xs={6} sm={3} key={index}>
                      <EventBox
                        imageSrc={moonPhaseImages[event.key]}
                        title={event.title}
                        time={event.time}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default LunarSolar;
